import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last line of defence so the app always renders *something*. If a render throws
 * — a bad backend response, blocked storage, a component bug — the operator sees
 * a readable screen with a way out, never a blank white page mid-shift.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surfaced for support; wire to a logger here if/when one is added.
    console.error('[bpc] uncaught render error:', error, info.componentStack);
  }

  private reload = () => {
    this.setState({ error: null });
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-base p-4">
        <div className="w-full max-w-md rounded-xl border border-line bg-surface p-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-danger/10 text-2xl text-danger ring-1 ring-danger/30">
            !
          </div>
          <h1 className="font-display text-xl font-semibold text-content">Something went wrong</h1>
          <p className="mx-auto mt-2 max-w-sm text-sm text-muted">
            The screen hit an unexpected error. Your saved data is safe — reload to continue. If it
            keeps happening, let support know.
          </p>
          <button
            onClick={this.reload}
            className="focus-ring mt-5 inline-flex h-10 items-center justify-center rounded-lg bg-brand px-5 text-sm font-semibold text-base hover:opacity-90"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
